import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pencil, Trash2, User, Plus, Mail, Phone } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const emptyForm = {
  id: "",
  full_name: "",
  current_profession: "",
  designation: "",
  photo_url: "",
  photo_public_id: "",
  email: "",
  mobile_no: "",
  order: 0,
  status: "Active",
};

export default function GoverningBody() {
  const { role } = useAuth();
  const canEdit = role === "institute_admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/institute/governing-body");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const openAdd = () => {
    setForm({ ...emptyForm, order: items.length });
    setOpen(true);
  };

  const openEdit = (m) => {
    setForm({ ...emptyForm, ...m });
    setOpen(true);
  };

  const save = async () => {
    if (!form.full_name.trim()) {
      toast.error("Full Name is required");
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Email address is invalid");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        current_profession: form.current_profession.trim(),
        designation: form.designation.trim(),
        photo_url: form.photo_url,
        photo_public_id: form.photo_public_id,
        email: form.email.trim(),
        mobile_no: form.mobile_no.trim(),
        order: Number(form.order) || 0,
        status: form.status,
      };
      if (form.id) {
        const { data } = await api.put(`/institute/governing-body/${form.id}`, payload);
        setItems((arr) => arr.map((it) => (it.id === data.id ? data : it)));
        toast.success("Member updated");
      } else {
        const { data } = await api.post("/institute/governing-body", payload);
        setItems((arr) => [...arr, data]);
        toast.success("Member added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const id = confirmDelete?.id;
    setConfirmDelete(null);
    if (!id) return;
    try {
      await api.delete(`/institute/governing-body/${id}`);
      setItems((arr) => arr.filter((it) => it.id !== id));
      toast.success("Member removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.full_name.localeCompare(b.full_name)),
    [items],
  );

  return (
    <div className="p-8 md:p-12 max-w-6xl mx-auto" data-testid="governing-body-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">About</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Governing Body</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Board members of the institute. Photos must be JPG / PNG / WebP, up to 300 KB. The list is
            shown on the public website under About → Governing Body.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={openAdd}
            data-testid="governing-body-add"
            className="inline-flex items-center gap-1.5 bg-[#234333] hover:bg-[#1a3527] text-white text-sm px-4 py-2.5 rounded-sm"
          >
            <Plus size={14} strokeWidth={2} /> Add Member
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : sortedItems.length === 0 ? (
        <div className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-12 text-center" data-testid="governing-body-empty">
          <Users2Placeholder />
          <div className="mt-3 text-[#6b655b]">No Governing Body members yet.</div>
          {canEdit && (
            <button
              onClick={openAdd}
              className="mt-4 inline-flex items-center gap-1.5 bg-[#234333] hover:bg-[#1a3527] text-white text-sm px-4 py-2 rounded-sm"
            >
              <Plus size={14} strokeWidth={2} /> Add the first member
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="governing-body-grid">
          {sortedItems.map((m) => (
            <div
              key={m.id}
              data-testid={`governing-body-card-${m.id}`}
              className="bg-white border border-[#e5e0d8] rounded-sm p-5 flex flex-col"
            >
              <div className="flex items-start gap-4 mb-3">
                <div className="w-16 h-16 rounded-sm bg-[#f9f8f6] border border-[#e5e0d8] overflow-hidden shrink-0 flex items-center justify-center text-[#234333]">
                  {m.photo_url ? (
                    <img src={m.photo_url} alt={m.full_name} className="w-full h-full object-cover" />
                  ) : (
                    <User size={24} strokeWidth={1.5} />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-lg text-[#1c1a17] leading-tight truncate">{m.full_name}</div>
                  {m.designation && (
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#234333] font-semibold mt-0.5">
                      {m.designation}
                    </div>
                  )}
                  {m.current_profession && (
                    <div className="text-xs text-[#6b655b] mt-1 line-clamp-2">{m.current_profession}</div>
                  )}
                  <div className="mt-2 text-[10px] uppercase tracking-wider">
                    <span className={`px-1.5 py-0.5 rounded-sm ${m.status === "Active" ? "bg-[#234333]/10 text-[#234333]" : "bg-[#6b655b]/10 text-[#6b655b]"}`}>
                      {m.status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="space-y-1 text-xs text-[#6b655b]">
                {m.email && (
                  <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 hover:text-[#234333] break-all">
                    <Mail size={12} strokeWidth={1.5} /> {m.email}
                  </a>
                )}
                {m.mobile_no && (
                  <a href={`tel:${m.mobile_no.replace(/\s+/g, "")}`} className="block inline-flex items-center gap-1.5 hover:text-[#234333]">
                    <Phone size={12} strokeWidth={1.5} /> {m.mobile_no}
                  </a>
                )}
              </div>
              {canEdit && (
                <div className="flex gap-1 pt-3 mt-auto border-t border-[#e5e0d8]">
                  <button
                    onClick={() => openEdit(m)}
                    data-testid={`governing-body-edit-${m.id}`}
                    className="inline-flex items-center gap-1 text-xs text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                  >
                    <Pencil size={12} strokeWidth={1.5} /> Edit
                  </button>
                  <button
                    onClick={() => setConfirmDelete(m)}
                    data-testid={`governing-body-delete-${m.id}`}
                    className="inline-flex items-center gap-1 text-xs text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                  >
                    <Trash2 size={12} strokeWidth={1.5} /> Remove
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#f9f8f6] max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="governing-body-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {form.id ? "Edit Member" : "Add Governing Body Member"}
            </DialogTitle>
            <DialogDescription className="text-[#6b655b]">
              Photos must be JPG / PNG / WebP and ≤ 300 KB. All other fields are free-form.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-[#1c1a17] mb-1 block">Full Name *</label>
              <input
                type="text"
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                className={inputCls}
                data-testid="governing-body-input-name"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#1c1a17] mb-1 block">Designation</label>
              <input
                type="text"
                placeholder="e.g. Chairman / Member / Secretary"
                value={form.designation}
                onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))}
                className={inputCls}
                data-testid="governing-body-input-designation"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#1c1a17] mb-1 block">Current Profession</label>
              <input
                type="text"
                placeholder="e.g. Industrialist, Educationist, Advocate"
                value={form.current_profession}
                onChange={(e) => setForm((f) => ({ ...f, current_profession: e.target.value }))}
                className={inputCls}
                data-testid="governing-body-input-profession"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#1c1a17] mb-1 block">Email ID</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className={inputCls}
                data-testid="governing-body-input-email"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#1c1a17] mb-1 block">Mobile No.</label>
              <input
                type="tel"
                value={form.mobile_no}
                onChange={(e) => setForm((f) => ({ ...f, mobile_no: e.target.value }))}
                className={inputCls}
                data-testid="governing-body-input-mobile"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#1c1a17] mb-1 block">Display Order</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm((f) => ({ ...f, order: e.target.value }))}
                className={inputCls}
                data-testid="governing-body-input-order"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#1c1a17] mb-1 block">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                className={inputCls}
                data-testid="governing-body-input-status"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-[#1c1a17] mb-1 block">Photo (≤ 300 KB)</label>
              <FileUploader
                kind="inst_gb_photo"
                label="Photo (≤ 300 KB)"
                url={form.photo_url}
                testid="governing-body-photo"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({ ...f, photo_url: url, photo_public_id: public_id }))
                }
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <button
              onClick={() => setOpen(false)}
              className="text-sm text-[#6b655b] hover:bg-[#efece6] px-4 py-2 rounded-sm"
              data-testid="governing-body-cancel"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="governing-body-save"
              className="bg-[#234333] hover:bg-[#1a3527] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : form.id ? "Save Changes" : "Add Member"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-[#f9f8f6]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Remove governing body member?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.full_name} will be removed from the public website. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white border-[#e5e0d8]">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="governing-body-confirm-delete"
              className="bg-[#9e2a2b] hover:bg-[#7a1f20] text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Users2Placeholder() {
  return (
    <div className="mx-auto w-12 h-12 rounded-full bg-[#efece6] flex items-center justify-center text-[#234333]">
      <User size={22} strokeWidth={1.5} />
    </div>
  );
}
